/**
 * Cross-license capacity report.
 *
 * `/reports/capacity` — staff-side overview of how much of each
 * license's locked-model capacity is in use. Surfaces:
 *
 *   • Top N licenses approaching their cap (≥ 80% utilisation)
 *   • Histogram of utilisation across the installed base
 *
 * Reads `/internal/licenses?limit=500` for the master list and
 * `/internal/licenses/:id/capacity` for each license's latest
 * snapshot. Fan-out is bounded by the limit; the dashboard isn't
 * meant to scale to 10k+ licenses without a server-side rollup
 * (separate sprint).
 *
 * Per `vloud/docs/vloud-license-server-v2-plan.md §5.3`.
 */

import { type JSX, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminApiClient, type LicenseRow } from '../api';
import { useSession } from '../session';
import { FetchState } from '../components/ui/FetchState';
import { failureFrom, type Failure } from '../lib/api-state';

/** pct is null when the utilisation cannot be computed (no limit, no snapshot). */
interface Dim { count: number | null; max: number | null; pct: number | null }
interface UtilisationRow {
  license: LicenseRow;
  instances:    Dim;
  linux_users:  Dim;
}

function limitOf(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}
function dim(count: number | null | undefined, max: number | null): Dim {
  if (count === null || count === undefined || max === null) {
    return { count: count ?? null, max, pct: null };
  }
  return { count, max, pct: Math.min(1, count / max) };
}

const TOP_N = 25;
const APPROACHING_THRESHOLD = 0.80;

export function CapacityReportPage(): JSX.Element {
  const session = useSession();
  const api = useMemo(
    () => new AdminApiClient({
      baseUrl: import.meta.env.VITE_LICENSE_SERVER_URL ?? '',
      getToken: () => session.state.token,
    }),
    [session.state.token],
  );

  const [rows, setRows] = useState<UtilisationRow[] | null>(null);
  const [err, setErr] = useState<Failure | null>(null);
  const [unreadable, setUnreadable] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null); setErr(null); setUnreadable(0);
    void (async () => {
      try {
        const list = await api.licenses({ limit: 500 });
        if (cancelled) return;
        const all = list.licenses;
        setProgress({ done: 0, total: all.length });

        // Bounded parallelism — at most 6 in flight to avoid hammering
        // the license-server.
        const out: UtilisationRow[] = [];
        let failed = 0;
        const queue = [...all];
        const workers = Array.from({ length: 6 }, async () => {
          while (queue.length > 0) {
            const lic = queue.shift();
            if (!lic) break;
            try {
              const cap = await api.licenseCapacity(lic.id, { limit: 1 });
              // The license list does not carry the limits. Take them from
              // the row if present, else from the license detail
              // (limits_json) - only for licenses that have a snapshot.
              const onRow = lic as unknown as { max_instances?: number; max_linux_users?: number };
              let maxInstances = limitOf(onRow.max_instances);
              let maxLinux = limitOf(onRow.max_linux_users);
              if (cap.latest && (maxInstances === null || maxLinux === null)) {
                const detail = await api.licenseDetail(lic.id);
                const lim = (detail.limits_json ?? {}) as Record<string, unknown>;
                maxInstances = maxInstances ?? limitOf(lim['max_instances']);
                maxLinux = maxLinux ?? limitOf(lim['max_linux_users']);
              }
              out.push({
                license: lic,
                instances: dim(cap.latest?.instances_count, maxInstances),
                linux_users: dim(cap.latest?.linux_users_count, maxLinux),
              });
            } catch (e) {
              failed += 1;
              failureFrom(e, 'a license capacity snapshot'); // logs detail
            }
            if (!cancelled) {
              setProgress((p) => p ? { ...p, done: p.done + 1 } : p);
            }
          }
        });
        await Promise.all(workers);

        if (cancelled) return;
        // Every license failed to read: that is an outage, not an empty fleet.
        if (all.length > 0 && out.length === 0 && failed === all.length) {
          setErr({
            kind: 'server', retryable: true,
            message: 'We could not read capacity data for any license. Try again in a moment.',
          });
          setProgress(null);
          return;
        }
        const worstPct = (r: UtilisationRow): number =>
          Math.max(r.instances.pct ?? -1, r.linux_users.pct ?? -1);
        out.sort((a, b) => worstPct(b) - worstPct(a));
        setUnreadable(failed);
        setRows(out);
        setProgress(null);
      } catch (e) {
        if (!cancelled) setErr(failureFrom(e, 'the license list'));
      }
    })();
    return () => { cancelled = true; };
  }, [api, reloadKey]);

  if (err) {
    return (
      <div>
        <h1 style={{ margin: '0 0 8px', fontSize: '1.25rem' }}>Capacity report</h1>
        <FetchState error={err} onRetry={() => setReloadKey((k) => k + 1)} />
      </div>
    );
  }
  if (!rows) {
    return (
      <div>
        <h1 style={{ margin: '0 0 8px', fontSize: '1.25rem' }}>Capacity report</h1>
        <p role="status" style={{ color: 'var(--text-muted)' }}>
          Loading… {progress ? `${progress.done} / ${progress.total}` : ''}
        </p>
      </div>
    );
  }

  // Only licenses with both a snapshot and a limit can be evaluated.
  const evaluated = rows.filter((r) => r.instances.pct !== null || r.linux_users.pct !== null);
  const notEvaluated = rows.length - evaluated.length;
  const approaching = evaluated.filter(
    (r) => (r.instances.pct ?? 0) >= APPROACHING_THRESHOLD || (r.linux_users.pct ?? 0) >= APPROACHING_THRESHOLD,
  );

  // Histogram buckets: 0-20%, 20-40%, ..., 100%+
  const buckets = [0, 0, 0, 0, 0, 0]; // last bucket = >100%
  for (const r of evaluated) {
    const worst = Math.max(r.instances.pct ?? 0, r.linux_users.pct ?? 0);
    const idx = worst >= 1 ? 5 : Math.min(4, Math.floor(worst * 5));
    buckets[idx]!++;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.25rem' }}>Capacity report</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: '4px 0 0' }}>
          Capacity use across {evaluated.length} {evaluated.length === 1 ? 'license' : 'licenses'}, from each license’s most recent
          usage snapshot compared with its limits.
        </p>
        {(notEvaluated > 0 || unreadable > 0) && (
          <p role="note" style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: '6px 0 0' }}>
            {notEvaluated > 0 && `${notEvaluated} license${notEvaluated === 1 ? ' has' : 's have'} no usage snapshot or no limit set and ${notEvaluated === 1 ? 'is' : 'are'} not included. `}
            {unreadable > 0 && `${unreadable} license${unreadable === 1 ? '' : 's'} could not be read. `}
            {unreadable > 0 && (
              <button type="button" onClick={() => setReloadKey((k) => k + 1)}>Retry</button>
            )}
          </p>
        )}
      </header>

      <section>
        <h2 style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 0.5,
                     color: 'var(--text-muted)', margin: '0 0 8px' }}>
          Approaching cap (≥ 80%)
        </h2>
        {evaluated.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>
            Utilisation can’t be shown yet: no license has both a usage snapshot and a limit.
          </p>
        ) : approaching.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>
            No licenses approaching their cap{notEvaluated + unreadable > 0 ? ' among the licenses with data.' : '.'}
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: 6 }}>License</th>
                <th style={{ padding: 6 }}>Tier</th>
                <th style={{ padding: 6 }}>Instances</th>
                <th style={{ padding: 6 }}>Linux users</th>
              </tr>
            </thead>
            <tbody>
              {approaching.slice(0, TOP_N).map((r) => (
                <tr key={r.license.id} style={{ borderBottom: '1px solid var(--border-subtle, var(--border))' }}>
                  <td style={{ padding: 6 }}>
                    <Link to={`/licenses/${r.license.id}`} style={{ color: 'var(--accent-text)' }}>
                      <code style={{ fontSize: '0.75rem' }}>{r.license.id.slice(0, 12)}…</code>
                    </Link>
                  </td>
                  <td style={{ padding: 6 }}>{r.license.plan_tier}</td>
                  <td style={{ padding: 6 }}>
                    <UtilisationBar pct={r.instances.pct} count={r.instances.count} max={r.instances.max} />
                  </td>
                  <td style={{ padding: 6 }}>
                    <UtilisationBar pct={r.linux_users.pct} count={r.linux_users.count} max={r.linux_users.max} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h2 style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 0.5,
                     color: 'var(--text-muted)', margin: '0 0 8px' }}>
          Utilisation distribution
        </h2>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120 }}>
          {buckets.map((n, i) => (
            <Histo key={i} label={['0–20%','20–40%','40–60%','60–80%','80–100%','>100%'][i] ?? ''}
                   value={n} max={Math.max(...buckets)} danger={i >= 4} />
          ))}
        </div>
      </section>
    </div>
  );
}

function UtilisationBar({ pct: pctIn, count, max }: Dim): JSX.Element {
  if (pctIn === null) {
    return (
      <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
        {count === null ? 'No snapshot' : `${count} / no limit set`}
      </span>
    );
  }
  const pct = pctIn;
  const tone = pct >= 1 ? 'var(--danger, red)'
             : pct >= 0.8 ? 'var(--warning, orange)'
             :              'var(--accent)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, pct * 100)}%`, height: '100%', background: tone }} />
      </div>
      <span style={{ minWidth: 64, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '0.75rem' }}>
        {count} / {max}
      </span>
    </div>
  );
}

function Histo({ label, value, max, danger }: { label: string; value: number; max: number; danger: boolean }): JSX.Element {
  const h = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
        <div style={{
          width: '100%',
          height: `${h}%`,
          background: danger ? 'var(--danger, red)' : 'var(--accent)',
          borderRadius: '4px 4px 0 0',
        }}/>
      </div>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: '0.875rem', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}
